import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getPlatformAPI } from '../lib/platform'
import type { Language } from '../lib/platform/types'

interface LanguageProviderContextProps {
  language: string
  changeLanguage: (language: string) => void
}

// Exportiert wie `AuthContext`: Storybook gibt einen festen Wert, ohne die Sprache zu speichern.
export const LanguageProviderContext = createContext<LanguageProviderContextProps | undefined>(undefined)

// Detect system/browser language
const detectSystemLanguage = (): string => {
  // Try to get language from navigator (works for both Web and Electron)
  const browserLang = navigator.language || (navigator as any).userLanguage

  if (browserLang) {
    // Extract language code (e.g., 'de-DE' -> 'de', 'en-US' -> 'en')
    const langCode = browserLang.split('-')[0].toLowerCase()

    // Check if we support this language
    const supportedLanguages = ['en', 'de']
    if (supportedLanguages.includes(langCode)) {
      return langCode
    }
  }

  // Fallback to English
  return 'en'
}

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const platformAPI = getPlatformAPI()

  const changeLanguage = useCallback(async (language: string) => {
    try {
      // Change language locally
      await i18n.changeLanguage(language)

      // Save via platform API
      await platformAPI.setLanguage(language as Language)
    } catch (error) {
      console.error('[LanguageProvider] Failed to change language:', error)
    }
  }, [i18n, platformAPI])

  // Listen for language changes from other windows (Electron only)
  useEffect(() => {
    if (platformAPI.platform === 'electron' && window.api) {
      const handleLanguageChange = (language: string) => {
        if (i18n.language !== language) {
          i18n.changeLanguage(language)
        }
      }

      window.api.receive('language-changed', handleLanguageChange)

      return () => {
        window.api.removeAllListeners('language-changed')
      }
    }
    return undefined
  }, [i18n, platformAPI])

  // Initialize language from platform on mount.
  // Guarded with a ref so the effect runs exactly once: react-i18next can
  // return a fresh `i18n` reference per render, and re-running this effect
  // mid-changeLanguage races with platformAPI.setLanguage and reverts the
  // user's selection by reading a stale stored value.
  const initRanRef = useRef(false)
  useEffect(() => {
    if (initRanRef.current) return
    initRanRef.current = true

    const initializeLanguage = async () => {
      try {
        const savedLanguage = await platformAPI.getLanguage()

        if (!savedLanguage) {
          const detectedLanguage = detectSystemLanguage()
          if (detectedLanguage && detectedLanguage !== i18n.language) {
            await i18n.changeLanguage(detectedLanguage)
            await platformAPI.setLanguage(detectedLanguage as Language)
          }
        } else if (savedLanguage !== i18n.language) {
          await i18n.changeLanguage(savedLanguage)
        }
      } catch (error) {
        console.error('[LanguageProvider] Failed to load language:', error)
      }
    }

    initializeLanguage()
  }, [i18n, platformAPI])

  const value = useMemo(() => ({
    language: i18n.language,
    changeLanguage
  }), [i18n.language, changeLanguage])

  return (
    <LanguageProviderContext.Provider value={value}>
      {children}
    </LanguageProviderContext.Provider>
  )
}

export function useLanguage() {
  const context = useContext(LanguageProviderContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}